import { useEffect, useState } from 'react';
import { T } from '@design/index';
import { govPageContainer, GOV_TYPE } from '../ui/PatientUIGovernor';
import { patientApi, type PrescriptionRecord } from '../api';

// The doctor panel stores medicines as loose records — normalize the common
// field spellings without inventing values.
const medName = (m: Record<string, unknown>) => String(m.medicineName ?? m.name ?? m.genericName ?? 'Medicine');
const medDosage = (m: Record<string, unknown>) => [m.strength, m.dosage].filter(Boolean).join(' ');
const medFrequency = (m: Record<string, unknown>) => String(m.frequency ?? m.foodTiming ?? '');
const medDuration = (m: Record<string, unknown>) => (m.duration ? String(m.duration) : '');

export default function PatientPrescriptions() {
  // Real signed prescriptions issued by doctors via the doctor panel.
  const [prescriptions, setPrescriptions] = useState<PrescriptionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reminderModal, setReminderModal] = useState<{ rxId: string; count: number } | null>(null);
  const [reminderError, setReminderError] = useState<string | null>(null);
  const [reminderBusy, setReminderBusy] = useState<string | null>(null);
  const [expandedRx, setExpandedRx] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    patientApi.getPrescriptions()
      .then((rows) => { if (alive) setPrescriptions(rows); })
      .catch(() => { if (alive) setLoadError('Prescriptions could not be loaded — check your connection and try again.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const handleOrderMedicines = (rxId: string) => {
    window.location.href = `/patient/care-services/pharmacy?prescriptionId=${rxId}`;
  };

  // Server-side creation (duplicate-safe there) — reminders land in the same
  // backend list the Medicines page reads.
  const handleAddReminders = async (rx: PrescriptionRecord) => {
    setReminderError(null);
    setReminderBusy(rx.id);
    try {
      const created = await patientApi.createRemindersFromPrescription(rx.id);
      setReminderModal({ rxId: rx.id, count: created.length });
    } catch {
      setReminderError('Reminders could not be created — the server is unreachable. Please try again.');
    } finally {
      setReminderBusy(null);
    }
  };

  const fmtDate = (d: string) => {
    try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div style={{ ...govPageContainer(), display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ ...GOV_TYPE.pageTitle }}>
          Prescriptions
        </div>
        <div style={{
          fontSize: 11, fontWeight: 600, color: T.teal, fontFamily: T.font,
          background: `${T.teal}12`, borderRadius: 10, padding: '2px 10px',
        }}>
          {prescriptions.length}
        </div>
      </div>

      {reminderError && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '10px 14px' }}>
          <span style={{ fontFamily: T.font, fontSize: 11, color: '#B91C1C', fontWeight: 600 }}>{reminderError}</span>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, fontFamily: T.font, fontSize: 12, color: T.textSec }}>
          Loading prescriptions…
        </div>
      ) : loadError ? (
        <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 16, padding: 24, textAlign: 'center' }}>
          <div style={{ fontFamily: T.font, fontSize: 12, color: '#B91C1C', fontWeight: 600 }}>{loadError}</div>
        </div>
      ) : prescriptions.length === 0 ? (
        <div style={{
          background: `${T.teal}08`, border: `1px solid ${T.border}`, borderRadius: 16,
          padding: 28, textAlign: 'center',
        }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>📋</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: T.font, marginBottom: 4 }}>
            No prescriptions yet
          </div>
          <div style={{ fontSize: 10, color: T.textTer, fontFamily: T.font, lineHeight: 1.6 }}>
            Prescriptions signed by your doctor on NeoHealthCard will appear here.
          </div>
          <button
            onClick={() => { window.location.href = '/patient/care-services/doctors'; }}
            style={{
              marginTop: 14, fontFamily: T.font, fontSize: 12, fontWeight: 600,
              color: T.white, background: T.teal, border: 'none', borderRadius: 10,
              padding: '8px 20px', cursor: 'pointer',
            }}
          >
            Book a Doctor Visit
          </button>
        </div>
      ) : (
        prescriptions.map(rx => {
          const isExpanded = expandedRx === rx.id;
          return (
            <div key={rx.id} style={{
              background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
              padding: 14, boxShadow: T.shadow,
            }}>
              {/* Doctor + date + eRx badge */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: T.font }}>
                      {rx.doctorName}
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: T.textSec, fontFamily: T.font, marginTop: 2 }}>
                    {rx.clinicName || ''}
                  </div>
                  <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font, marginTop: 1 }}>
                    {fmtDate(rx.signedAt || rx.createdAt)}
                  </div>
                </div>
                {/* These come from the doctor panel and are only listed once
                    the doctor SIGNED them — that is the real status. */}
                <div style={{
                  fontSize: 8, fontWeight: 600, color: '#065F46', fontFamily: T.font,
                  background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 6, padding: '2px 8px',
                }}>
                  Signed by doctor
                </div>
              </div>

              {/* Diagnosis */}
              {rx.diagnosis && (
                <div style={{
                  fontSize: 10, color: T.textSec, fontFamily: T.font,
                  marginBottom: 8, padding: '5px 10px', background: T.ice, borderRadius: 8,
                  borderLeft: `3px solid ${T.teal}`,
                }}>
                  <span style={{ fontWeight: 600, color: T.text }}>Diagnosis:</span> {rx.diagnosis}
                </div>
              )}

              {/* Medicines list */}
              <div style={{
                borderTop: `1px solid ${T.border}`, paddingTop: 8, marginBottom: 10,
              }}>
                <div style={{ fontSize: 9, fontWeight: 600, color: T.textTer, fontFamily: T.font, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                  Medicines ({rx.medicines.length})
                </div>
                {rx.medicines.slice(0, isExpanded ? rx.medicines.length : 3).map((med, idx) => (
                  <div key={idx} style={{
                    padding: '6px 0', borderBottom: idx < rx.medicines.length - 1 ? `1px solid ${T.border}40` : 'none',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: T.font }}>
                        💊 {medName(med)}
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: T.teal, fontFamily: T.font }}>
                        {medDosage(med)}
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: T.textSec, fontFamily: T.font, marginTop: 2 }}>
                      {[medFrequency(med), medDuration(med)].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                ))}
                {rx.medicines.length > 3 && !isExpanded && (
                  <button
                    onClick={() => setExpandedRx(rx.id)}
                    style={{
                      fontFamily: T.font, fontSize: 10, fontWeight: 600, color: T.teal,
                      background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
                    }}
                  >
                    +{rx.medicines.length - 3} more medicines
                  </button>
                )}
                {isExpanded && rx.medicines.length > 3 && (
                  <button
                    onClick={() => setExpandedRx(null)}
                    style={{
                      fontFamily: T.font, fontSize: 10, fontWeight: 600, color: T.teal,
                      background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
                    }}
                  >
                    Show less
                  </button>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => handleOrderMedicines(rx.id)}
                  style={{
                    flex: 1, fontSize: 11, fontWeight: 600, fontFamily: T.font,
                    color: T.white, background: '#059669',
                    border: 'none', borderRadius: 10, padding: '9px 0', cursor: 'pointer',
                  }}
                >
                  Order Medicines
                </button>
                <button
                  onClick={() => handleAddReminders(rx)}
                  disabled={reminderBusy === rx.id}
                  style={{
                    flex: 1, fontSize: 11, fontWeight: 600, fontFamily: T.font,
                    color: T.teal, background: 'transparent', border: `1.5px solid ${T.teal}`,
                    borderRadius: 10, padding: '9px 0', cursor: 'pointer',
                    opacity: reminderBusy === rx.id ? 0.6 : 1,
                  }}
                >
                  {reminderBusy === rx.id ? 'Adding…' : 'Add Reminders'}
                </button>
              </div>
            </div>
          );
        })
      )}

      {/* Reminder success modal */}
      {reminderModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}>
          <div style={{
            background: T.white, borderRadius: 20, padding: 28, maxWidth: 340, width: '100%',
            textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text, fontFamily: T.font, marginBottom: 6 }}>
              Reminders Created!
            </div>
            <div style={{ fontSize: 12, color: T.textSec, fontFamily: T.font, marginBottom: 4 }}>
              {reminderModal.count > 0
                ? `${reminderModal.count} medicine reminder${reminderModal.count !== 1 ? 's' : ''} created from this prescription.`
                : 'All medicines from this prescription were already imported — nothing was duplicated.'}
            </div>
            <div style={{ fontSize: 10, color: T.textTer, fontFamily: T.font, marginBottom: 16 }}>
              Manage times and doses on the Medicines page.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={() => { setReminderModal(null); window.location.href = '/patient/medicines'; }}
                style={{
                  fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.white,
                  background: T.teal, border: 'none', borderRadius: 12,
                  padding: '11px 0', cursor: 'pointer', width: '100%',
                }}
              >
                View Medicine Reminders
              </button>
              <button
                onClick={() => setReminderModal(null)}
                style={{
                  fontFamily: T.font, fontSize: 12, fontWeight: 600, color: T.textSec,
                  background: T.ice, border: 'none', borderRadius: 12,
                  padding: '10px 0', cursor: 'pointer', width: '100%',
                }}
              >
                Stay on Prescriptions
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
